<script setup lang="ts">
import Tag from '@components/Tag.vue';
import type { Submenu } from '@global-types/nav-v2/menuItems';
import SubmenuItem from '@layout/nav-v2/SubmenuItem.vue';
import { computed } from 'vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Icon from '@components/Icon.vue';
import IconCaretDown from '@icons/ph_caret-down.svg';
import Typography from '@components/Typography.vue';
import { trans } from '@elements/translation.ts';
import PlusMenuItem from '@layout/nav-v2/mobile/components/PlusMenuItem.vue';
import MenuList from '@layout/nav-v2/mobile/components/MenuList.vue';

interface MenuLinkProps {
  isDropdownOpen: boolean,
  hasPin?: boolean,
  hasNewTag?: boolean,
  submenus: Submenu[],
}

const props = withDefaults(defineProps<MenuLinkProps>(), {
  hasPin: false,
  hasNewTag: false,
});

defineSlots<{
  icon(iconProps: { isActive: boolean }): typeof Icon,
  text(): unknown,
}>();

const emit = defineEmits<{(e: 'toggleDropdown', isOpen: boolean): void }>();

const { isCurrentPage } = useNavbarStore();

const isDropdownOpen = computed<boolean>({
  get: () => props.isDropdownOpen,
  set: (isOpen) => emit('toggleDropdown', isOpen),
});

const isActive = computed<boolean>(() => props.submenus.some((submenu) => isCurrentPage(submenu.link)));

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};
</script>

<template>
  <div>
    <PlusMenuItem
      :is-active="isActive && !isDropdownOpen"
      :has-pin="hasPin"
      @click="toggleDropdown"
    >
      <template #icon>
        <slot name="icon" :is-active="isActive || isDropdownOpen" />
      </template>

      <template #text>
        <div class="flex items-center justify-between">
          <Typography
            :variant="isDropdownOpen ? 'body-bold' : 'body'"
            class="whitespace-nowrap"
          >
            <slot name="text" />
          </Typography>

          <div class="flex space-x-2">
            <Tag
              v-if="hasNewTag"
              text-color="purple"
              background-color="purple-lighter"
            >
              {{ trans('gestion.tag.new') }}
            </Tag>

            <Icon
              :svg="IconCaretDown"
              height="24"
              width="24"
              :fill="isActive ? 'black' : 'gray-darker'"
              :class="[
                'transition-transform',
                'duration-200',
                { 'rotate-180': isDropdownOpen },
              ]"
            />
          </div>
        </div>
      </template>
    </PlusMenuItem>

    <MenuList
      v-if="isDropdownOpen"
      class="plus-menu-item-group__dropdown-open"
      :class="[
        'ml-4',
        'p-2 pr-0',
        'flex flex-col',
      ]"
    >
      <li
        v-for="submenu in submenus"
        :key="submenu.name"
      >
        <SubmenuItem
          :link="submenu.link"
          :is-active="isCurrentPage(submenu.link)"
        >
          <Typography :variant="isCurrentPage(submenu.link) ? 'body-bold' : 'body'">
            {{ submenu.name }}
          </Typography>
        </SubmenuItem>
      </li>
    </MenuList>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables/colors' as *;
@use '@styles/mixins/rem' as *;

.plus-menu-item-group {
  &__dropdown-open {
    border-left: toRem(2) solid $colorGrayLight;
  }
}
</style>
